import type {
  CampaignBodyFormat,
  CampaignButton,
  CampaignFormat,
  CampaignPlacement,
  CampaignType,
} from '@/lib/admin/messageCampaigns';

export type CampaignSurface = 'portal' | 'mobile_app';

export interface LiveCampaign {
  id: string;
  title: string;
  message: string;
  bodyFormat: CampaignBodyFormat;
  type: CampaignType;
  typeLabel?: string | null;
  placement: CampaignPlacement;
  format: CampaignFormat;
  mediaUrl: string | null;
  buttons: CampaignButton[];
  publishAt: string | null;
  expiresAt: string | null;
}

export const CAMPAIGN_PLACEMENT_PRIORITY: CampaignPlacement[] = [
  'dialog_box',
  'top_banner',
  'bottom_right',
];

export function pickActiveCampaign(
  campaigns: LiveCampaign[],
  { canRenderBanner }: { canRenderBanner: boolean },
): LiveCampaign | null {
  if (!Array.isArray(campaigns)) return null;
  for (const placement of CAMPAIGN_PLACEMENT_PRIORITY) {
    if (placement === 'top_banner' && !canRenderBanner) continue;
    const match = campaigns.find((campaign) => campaign?.placement === placement);
    if (match) return match;
  }
  return null;
}

export function isInternalUrl(url: string): boolean {
  const trimmed = String(url || '').trim();
  if (!trimmed.startsWith('/')) return false;
  return !trimmed.startsWith('//');
}

export function isSafeCampaignUrl(url: unknown): url is string {
  if (typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  if (isInternalUrl(trimmed)) return true;
  return /^https?:\/\//i.test(trimmed);
}

export function renderableButtons(buttons: CampaignButton[] | undefined): CampaignButton[] {
  if (!Array.isArray(buttons)) return [];
  return buttons.filter((button) => !!button?.label?.trim() && isSafeCampaignUrl(button?.url));
}

export function campaignBodyText(message: string, bodyFormat: CampaignBodyFormat): string {
  const raw = bodyFormat === 'html' ? String(message || '').replace(/<[^>]*>/g, ' ') : message;
  return String(raw || '').replace(/\s+/g, ' ').trim();
}


export interface CampaignBodyListItem {
  title: string;
  text: string;
}

export type CampaignBodyBlock =
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; items: CampaignBodyListItem[] };

const BULLET_LEAD =
  /^(?:[•·▪◦‣]\s*|[*-]\s+|\p{Extended_Pictographic}(?:️|‍\p{Extended_Pictographic}|[\u{1F3FB}-\u{1F3FF}])*\s+)/u;

const TITLE_SPLIT = /\s+[—–]\s+/;

export function campaignBodyBlocks(
  message: string,
  bodyFormat: CampaignBodyFormat,
): CampaignBodyBlock[] {
  let raw = String(message || '');

  if (bodyFormat === 'html') {
    raw = raw
      .replace(/<\s*br\s*\/?\s*>/gi, '\n')
      .replace(/<\/\s*(p|div|li|ul|ol|h[1-6]|tr)\s*>/gi, '\n')
      .replace(/<\s*li[^>]*>/gi, '\n• ')
      .replace(/<[^>]*>/g, ' ');
  }

  const normalised = raw
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .join('\n')
    .replace(/\s+([•·▪◦‣])\s+/g, '\n$1 ');

  const blocks: CampaignBodyBlock[] = [];
  let run: CampaignBodyListItem[] = [];
  let awaitingText: CampaignBodyListItem | null = null;

  const flush = () => {
    if (run.length > 0) {
      blocks.push({ kind: 'list', items: run });
      run = [];
    }
    awaitingText = null;
  };

  for (const line of normalised.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const bullet = trimmed.match(BULLET_LEAD);
    if (!bullet) {
      if (awaitingText) {
        awaitingText.title = awaitingText.text;
        awaitingText.text = trimmed;
        awaitingText = null;
        continue;
      }
      flush();
      blocks.push({ kind: 'paragraph', text: trimmed });
      continue;
    }

    awaitingText = null;
    const rest = trimmed.slice(bullet[0].length).trim();
    if (!rest) continue; // A lone "•" is a typo, not a row.

    const split = rest.split(TITLE_SPLIT);
    if (split.length > 1) {
      run.push({ title: split[0].trim(), text: split.slice(1).join(' — ').trim() });
    } else {
      const item: CampaignBodyListItem = { title: '', text: rest };
      run.push(item);
      awaitingText = item;
    }
  }
  flush();

  return blocks;
}

export interface CampaignTitleParts {
  lead: string;
  accent: string;
}

const TRAILING_ARROW = /[→⇒➔➜➞»›>]\s*$/u;

export function labelEndsWithArrow(label: string): boolean {
  return TRAILING_ARROW.test(String(label || ''));
}

export function campaignRowLead(title: string): string {
  const trimmed = String(title || '').trim();
  if (!trimmed) return '';
  return /[:：?!.…—–]$/u.test(trimmed) ? trimmed : `${trimmed}:`;
}

export function splitCampaignTitle(title: string): CampaignTitleParts {
  const whole = String(title || '').replace(/\s+/g, ' ').trim();
  const match = whole.match(TITLE_SPLIT);
  if (!match || match.index === undefined) return { lead: whole, accent: '' };

  const lead = whole.slice(0, match.index).trim();
  const accent = whole.slice(match.index + match[0].length).trim();
  if (!lead || !accent) return { lead: whole, accent: '' };
  return { lead, accent };
}
